import React, { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

import posthog from 'posthog-js';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TransitionProps } from '@mui/material/transitions';
import TextField from '@mui/material/TextField';
import {
  SensorHistoryPlotItem,
  SensorMetricPlotItem,
  TimeRange,
  isHistoryData,
  isMetricData,
} from '../Plots/plotCommon';
import useStyles from '../../styles';
import { themeProps } from '../../styles/theme';
import { prepareCSVzip, prepareMetricCSV } from '../../utils/dataDownload';
import {
  getActivePlotVars,
  getGhostParams,
  getSelectedEndDate,
  getSelectedHours,
  getSelectedSensorIds,
  getSelectedStartDate,
  getSensorsById,
  getShowAdvancedAnalysis,
} from '../../state/selectors';
import Report, { statsPerVarNamePerSensor } from '../../components/ComparePageReport';
import StatsBox from './StatsBox';
import { setGhostParams, toggleShowAdvancedAnalysis } from '../../state/actions';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { filterHistoryCSVdata, filterMetricsCSVData, MetricOptions } from './helpers';

interface CompareReportProps {
  sensorData: SensorHistoryPlotItem[] | SensorMetricPlotItem[];
  fetchingSensorData: boolean;
  unselectedTimes: TimeRange[] | undefined;
  metricSelection: MetricOptions;
}

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
    // eslint-disable-next-line react/jsx-props-no-spreading
  ) => <Slide direction="up" ref={ref} {...props} />
);

function CompareReport({
  sensorData,
  fetchingSensorData,
  unselectedTimes,
  metricSelection,
}: CompareReportProps): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [reportOpen, setReportOpen] = useState(false);
  const selectedSensors = useAppSelector(getSelectedSensorIds);
  const startDate = useAppSelector(getSelectedStartDate);
  const endDate = useAppSelector(getSelectedEndDate);
  const selectedVarNames = useAppSelector(getActivePlotVars);
  const selectedHours = useAppSelector(getSelectedHours);
  const ghostParams = useAppSelector(getGhostParams);
  const showAdvancedAnalysis = useAppSelector(getShowAdvancedAnalysis);
  const sensorsById = useAppSelector(getSensorsById);
  const { addGhosts, weekCount } = ghostParams;

  // Selected hours only if option is selected
  const { selectHours } = selectedHours;
  let { startHour, endHour, includeWeekends } = selectedHours;
  if (!selectHours) {
    startHour = 0;
    endHour = 24;
    includeWeekends = true;
  }

  const prepareReport = () => {
    setReportOpen(true);
  };

  const prepareDownload = () => {
    if (isHistoryData(sensorData)) {
      const filteredData: SensorHistoryPlotItem[] = filterHistoryCSVdata(selectedHours, sensorData, sensorsById);
      prepareCSVzip(filteredData);
      posthog.capture('CSV downloaded from compare page');
    }
    if (isMetricData(sensorData)) {
      const filteredData: SensorMetricPlotItem[] = filterMetricsCSVData(
        selectedHours,
        sensorData,
        sensorsById
      );
      prepareMetricCSV(filteredData, selectedVarNames, ghostParams, selectedHours);
      posthog.capture('Metric CSV downloaded from compare page');
    }
  };

  const handleClose = () => {
    setReportOpen(false);
  };

  const stats = useMemo(
    () => statsPerVarNamePerSensor(sensorData, [startHour, endHour], includeWeekends),
    [sensorData, startHour, endHour, includeWeekends]
  );

  const handleGhostOffset = (offset: number) =>
    dispatch(setGhostParams({ ...ghostParams, weekCount: offset }));

  const ghostDateLabel = useMemo(() => {
    let label = '';
    const ghostTimeOffsetVal = weekCount * 7 * 24 * 3600;
    const ghostStart = new Date(startDate).getTime() - ghostTimeOffsetVal * 1000;
    const ghostEnd = new Date(endDate).getTime() - ghostTimeOffsetVal * 1000;
    if (ghostStart && ghostEnd)
      label = `${dayjs(ghostStart).format('ll')}
    - ${dayjs(ghostEnd).format('ll')} `;
    return label;
  }, [endDate, startDate, weekCount]);

  return (
    <Box>
      <Accordion
        style={{
          width: '100%',
          margin: '1rem 0 1.5rem 0',
          background: theme.palette.primary.light,
        }}
        expanded={showAdvancedAnalysis}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              onClick={() => dispatch(toggleShowAdvancedAnalysis(!showAdvancedAnalysis))}
            />
          }
        >
          <Box sx={{ width: '100%' }} className={classes.spaceBetweenFlex}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon />
              <Typography variant="h6" style={{ marginLeft: '10px' }}>
                Advanced Analysis
                <Typography variant="body2">
                  {`${dayjs(startDate).format('ll')}
            - ${dayjs(endDate).format('ll')} `}
                </Typography>
              </Typography>
            </Box>

            {showAdvancedAnalysis && (
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ margin: { xs: '0 0 0 20px', lg: ' 0 20px' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={addGhosts}
                        onChange={() =>
                          dispatch(setGhostParams({ ...ghostParams, addGhosts: !addGhosts }))
                        }
                        style={{ padding: '0 9px' }}
                      />
                    }
                    label="Show Ghosts"
                  />
                  <Typography variant="body2">{ghostDateLabel}</Typography>
                </Box>
                <TextField
                  type="number"
                  label="No. of Weeks"
                  style={{ textTransform: 'capitalize', width: '100px' }}
                  value={weekCount}
                  onChange={(e) => handleGhostOffset(Number(e.target.value))}
                  variant="standard"
                  size="small"
                  disabled={!addGhosts}
                />
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <Box sx={{ padding: { lg: '1rem', xs: '0.5rem' } }}>
          <StatsBox stats={stats} />
          <FormGroup row className={classes.reportToolbar}>
            <Button
              variant={themeProps.btnVariant.default}
              onClick={prepareReport}
              startIcon={<AssignmentIcon>prepare report</AssignmentIcon>}
              className={classes.reportButton}
            >
              View Report
            </Button>
            <Tooltip title="Download data in .csv format">
              <span>
                <Button
                  variant={themeProps.btnVariant.default}
                  onClick={prepareDownload}
                  startIcon={<SaveAltIcon />}
                  className={classes.reportButton}
                  disabled={selectedSensors.length === 0}
                >
                  Download CSV
                </Button>
              </span>
            </Tooltip>
          </FormGroup>
        </Box>
      </Accordion>
      <Dialog
        fullScreen
        open={reportOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        style={{ zIndex: 100 }}
      >
        <Report
          sensorData={sensorData}
          stats={stats}
          dateRange={[startDate, endDate]}
          selectedHours={[startHour, endHour]}
          includeWeekends={includeWeekends}
          onClose={handleClose}
          fetchingSensorData={fetchingSensorData}
          unselectedTimes={unselectedTimes}
          metricSelection={metricSelection}
        />
      </Dialog>
    </Box>
  );
}

export default CompareReport;
